import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import { I18nContext, type I18nContextValue, type I18nScope } from './context';
import { createFormatters, getActiveLocale, setActiveLocale } from './format';
import { DEFAULT_LANGUAGE, LANGUAGE_CODE_PATTERN, languageMeta } from './languages';
import { isLanguageLoaded, loadedDictionaries, loadLanguage } from './loader';
import { createTranslator } from './translate';

export interface I18nProviderProps {
  children: ReactNode;
  /** Controlled language. When set, `setLanguage` only calls `onLanguageChange`. */
  language?: string | null;
  /** Uncontrolled starting language. */
  initialLanguage?: string | null;
  onLanguageChange?: (code: string) => void;
  /** Which surface this provider serves. Default `admin`. */
  scope?: I18nScope;
  /**
   * This provider owns the page: set `<html lang dir>` and the active locale
   * `lib/money.ts` formats with. Leave off for nested providers (a storefront
   * preview inside the dashboard) so they do not fight the page's own.
   */
  applyToDocument?: boolean;
  /**
   * Wording variants to prefer, most specific first - `['clinics',
   * 'sector_services']` makes a clinic's "Products" read "Our services". See
   * "Variants" in `translate.ts`.
   */
  variants?: readonly string[];
}

const NO_VARIANTS: readonly string[] = [];

function normaliseCode(code: string | null | undefined): string {
  const candidate = (code ?? '').trim().toLowerCase().replace(/_/g, '-');
  return LANGUAGE_CODE_PATTERN.test(candidate) ? candidate : DEFAULT_LANGUAGE;
}

/**
 * Supplies `useI18n()` / `useT()` for a subtree.
 *
 * Switching to a language whose dictionaries are not loaded yet keeps
 * rendering the previous language (`ready: false`) until the chunk arrives,
 * then swaps in one render - no flash of keys or of half-translated screens.
 * A language with no dictionaries in this build (or whose chunk failed) still
 * switches: its locale, direction and formatting apply, and text falls back
 * to English key by key.
 */
export function I18nProvider({
  children,
  language,
  initialLanguage,
  onLanguageChange,
  scope = 'admin',
  applyToDocument = false,
  variants = NO_VARIANTS,
}: I18nProviderProps) {
  const controlled = language !== undefined && language !== null;
  const [internal, setInternal] = useState(() => normaliseCode(initialLanguage));
  const requested = controlled ? normaliseCode(language) : internal;

  // The language on screen. Trails `requested` only while it loads.
  const [shown, setShown] = useState(() => (isLanguageLoaded(requested) ? requested : DEFAULT_LANGUAGE));

  useEffect(() => {
    if (isLanguageLoaded(requested)) {
      setShown(requested);
      return;
    }
    let cancelled = false;
    void loadLanguage(requested).then(() => {
      if (!cancelled) setShown(requested);
    });
    return () => {
      cancelled = true;
    };
  }, [requested]);

  const active = isLanguageLoaded(requested) ? requested : shown;
  const ready = active === requested;
  const meta = languageMeta(active);

  const setLanguage = useCallback(
    (code: string) => {
      const next = normaliseCode(code);
      if (!controlled) setInternal(next);
      onLanguageChange?.(next);
    },
    [controlled, onLanguageChange],
  );

  useEffect(() => {
    if (!applyToDocument || typeof document === 'undefined') return;
    const root = document.documentElement;
    const previous = { lang: root.lang, dir: root.dir, locale: getActiveLocale() };
    root.lang = meta.locale;
    root.dir = meta.direction;
    setActiveLocale(meta.locale);
    return () => {
      root.lang = previous.lang;
      root.dir = previous.dir;
      setActiveLocale(previous.locale);
    };
  }, [applyToDocument, meta.locale, meta.direction]);

  // `loadedDictionaries()` is the loader's live registry, so the translator
  // sees a namespace that finishes loading without being rebuilt; it only has
  // to change when the language does.
  // Keyed on the variants' contents, so a caller building a fresh array each
  // render does not rebuild `t` (and re-render every consumer) each time.
  const variantsKey = variants.join('|');
  const stableVariants = useMemo(() => (variantsKey ? variantsKey.split('|') : NO_VARIANTS), [variantsKey]);
  const t = useMemo(
    () => createTranslator(loadedDictionaries(), [active, DEFAULT_LANGUAGE], stableVariants),
    [active, stableVariants],
  );
  const formatters = useMemo(() => createFormatters(meta.locale), [meta.locale]);

  const value = useMemo<I18nContextValue>(
    () => ({
      language: active,
      locale: meta.locale,
      direction: meta.direction,
      scope,
      variants: stableVariants,
      t,
      setLanguage,
      ready,
      formatters,
    }),
    [active, meta.locale, meta.direction, scope, stableVariants, t, setLanguage, ready, formatters],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}
